import { supabase } from '@/integrations/supabase/client';

interface BehaviorEvent {
  action_type: string;
  app_name?: string;
  metadata?: any;
  session_id: string;
  device_type: string;
  timestamp: number;
}

class BehaviorTrackingQueue {
  private queue: BehaviorEvent[] = [];
  private readonly MAX_QUEUE_SIZE = 50; // Increased from 25 to 50
  private readonly FLUSH_INTERVAL = 30000; // Increased from 15s to 30s
  private flushTimer: NodeJS.Timeout | null = null;
  private sessionId: string = crypto.randomUUID();

  constructor() {
    // Flush on page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => this.flushSync());
    }
  }

  addEvent(actionType: string, appName?: string, metadata?: any) {
    const event: BehaviorEvent = {
      action_type: actionType,
      app_name: appName,
      metadata: metadata || {},
      session_id: this.sessionId,
      device_type: /Mobile|Tablet/.test(navigator.userAgent) ? 'mobile' : 'desktop',
      timestamp: Date.now(),
    };

    this.queue.push(event);

    // Flush if queue is full
    if (this.queue.length >= this.MAX_QUEUE_SIZE) {
      this.flush();
    } else {
      // Reset flush timer
      this.scheduleFlush();
    }
  }

  private scheduleFlush() {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
    }

    this.flushTimer = setTimeout(() => {
      this.flush();
    }, this.FLUSH_INTERVAL);
  }

  async flush() {
    if (this.queue.length === 0) return;

    const eventsToSend = [...this.queue];
    this.queue = [];

    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    try {
      // Check if user is authenticated before sending
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.warn('No active session, discarding behavior tracking queue');
        return;
      }

      await supabase.functions.invoke('track-user-behavior', {
        body: { events: eventsToSend },
      });
    } catch (error) {
      console.error('Error flushing behavior tracking queue:', error);
      // Re-queue events on error only if it's not an auth error
      if (error && !error.toString().includes('Not authenticated')) {
        this.queue.unshift(...eventsToSend);
      }
    }
  }

  flushSync() {
    if (this.queue.length === 0) return;

    const eventsToSend = [...this.queue];
    this.queue = [];

    // Use fetch with keepalive for page unload (includes auth headers)
    supabase.functions.invoke('track-user-behavior', {
      body: { events: eventsToSend },
    }).catch(err => {
      console.error('Error sending events on unload:', err);
    });
  }

  resetSession() {
    this.sessionId = crypto.randomUUID();
  }

  getQueueStats() {
    return {
      queueSize: this.queue.length,
      sessionId: this.sessionId,
    };
  }
}

// Export singleton instance
export const trackingQueue = new BehaviorTrackingQueue();
